import { createGlobalStyle } from "styled-components";
import { cssVariables, layers } from "./tokens";

// tokens → base → motion → third-party overrides, in that order.
// Components reference these keyframes by name and never define their own.
export const GlobalStyles = createGlobalStyle`
  :root {
    ${cssVariables}

    --card-size: 200px;
    --card-font: 1.05rem;

    color-scheme: light;
  }

  @media screen and (max-width: 600px) {
    :root {
      --card-size: 150px;
      --card-font: 0.875rem;
    }
  }

  /* ---------- base ---------- */

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  html,
  body,
  div#root {
    padding: 0;
    margin: 0;
    width: 100%;
    max-width: 100vw;
    min-height: 100%;
    min-height: -webkit-fill-available;
    position: relative;
  }

  body {
    font-family: var(--font-body);
    font-weight: 500;
    color: var(--ink);
    background-color: var(--paper);
    background-image: radial-gradient(var(--paper-dot) 1.2px, transparent 1.2px);
    background-size: 22px 22px;
    background-attachment: fixed;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    -webkit-tap-highlight-color: transparent;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-family: var(--font-display);
    color: var(--ink);
    margin: 0;
  }

  ::selection {
    background: var(--gold-tint);
    color: var(--ink);
  }

  :focus-visible {
    outline: 3px solid var(--focus-ring);
    outline-offset: 2px;
  }

  /* ---------- motion ---------- */

  @keyframes pop-in {
    0% { opacity: 0; transform: scale(0.85); }
    60% { opacity: 1; transform: scale(1.04); }
    100% { opacity: 1; transform: scale(1); }
  }

  @keyframes wiggle {
    0%, 100% { transform: rotate(0deg); }
    25% { transform: rotate(-4deg); }
    75% { transform: rotate(4deg); }
  }

  @keyframes bob {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-3px); }
  }

  @keyframes float {
    0%, 100% { transform: translate(0, 0) rotate(var(--tilt, 0deg)); }
    50% { transform: translate(6px, -14px) rotate(calc(var(--tilt, 0deg) + 8deg)); }
  }

  @keyframes pulse-ring {
    0% { box-shadow: 0 0 0 0 var(--ring, var(--gold)); }
    100% { box-shadow: 0 0 0 10px transparent; }
  }

  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    20%, 60% { transform: translateX(-3px); }
    40%, 80% { transform: translateX(3px); }
  }

  @keyframes draw-in {
    to { stroke-dashoffset: 0; }
  }

  @keyframes tada {
    0% { transform: scale(0.6) rotate(-6deg); opacity: 0; }
    50% { transform: scale(1.08) rotate(3deg); opacity: 1; }
    75% { transform: scale(0.97) rotate(-1deg); }
    100% { transform: scale(1) rotate(0deg); }
  }

  @keyframes confetti-fall {
    0% { transform: translate3d(0, -10vh, 0) rotate(0deg); opacity: 1; }
    100% { transform: translate3d(var(--drift, 0px), 105vh, 0) rotate(var(--spin, 540deg)); opacity: 0.9; }
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .anim-pop { animation: pop-in var(--dur-slow) var(--ease-bounce) both; }
  .anim-wiggle { animation: wiggle 600ms var(--ease-out); }
  .anim-bob { animation: bob 1.8s ease-in-out infinite; }

  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }

  /* ---------- toasts (material-react-toastify) ---------- */

  /* Scoped to the wrapper so it wins over the library's :root default. */
  .Toastify {
    --toastify-z-index: ${layers.toast};
  }

  .Toastify__toast-container {
    padding: var(--space-2);
  }

  .Toastify__toast {
    font-family: var(--font-body);
    font-weight: 700;
    font-size: var(--text-sm);
    color: var(--ink);
    background: var(--card);
    border: var(--border-w) solid var(--ink);
    border-radius: var(--radius-lg);
    box-shadow: 0 var(--lip) 0 var(--line-strong), var(--shadow-float);
    min-height: 52px;
    padding: var(--space-2) var(--space-4);
    /* No animation here: the library's enter/exit animations drive removal. */
  }

  .Toastify__toast-body {
    margin: 0;
    padding: 0;
  }

  .Toastify__toast--dark {
    background: var(--night);
    color: var(--night-ink);
    border-color: var(--night-lip);
    box-shadow: 0 var(--lip) 0 var(--night-lip), var(--shadow-float);
  }

  .Toastify__toast--success {
    background: var(--teal-tint);
    color: var(--teal-shade);
    border-color: var(--teal-shade);
    box-shadow: 0 var(--lip) 0 var(--teal), var(--shadow-float);
  }

  .Toastify__toast--error {
    background: var(--cherry-tint);
    color: var(--cherry-shade);
    border-color: var(--cherry-shade);
    box-shadow: 0 var(--lip) 0 var(--cherry), var(--shadow-float);
  }

  .Toastify__toast--warning {
    background: var(--gold-tint);
    color: var(--warn);
    border-color: var(--warn);
    box-shadow: 0 var(--lip) 0 var(--gold), var(--shadow-float);
  }

  .Toastify__toast--info {
    background: var(--teal-tint);
    color: var(--teal-shade);
    border-color: var(--teal-shade);
    box-shadow: 0 var(--lip) 0 var(--teal), var(--shadow-float);
  }

  .Toastify__close-button {
    color: currentColor;
    opacity: 0.6;
    align-self: center;
  }

  .Toastify__progress-bar {
    background: currentColor;
    opacity: 0.25 !important;
    height: 3px;
  }
`;
